import { getBytesFormatters } from './byteFormatters';
import { DateFormatterInput, getDateFormatters } from './dateFormatters';
import { getListFormatters } from './listFormatters';
import { memoOne } from './memoOne';
import { getNumericFormatters } from './numericFormatters';
import { DurationProps, getRelativeTimeFormatters } from './relativeTimeFormatters';

export type FormatterMap<T> = Partial<Record<string, (value: T) => string>>;

export const getFormatterMaps = memoOne((lng: string) => {
  const numericFormatters = getNumericFormatters(lng);
  const byteFormatters = getBytesFormatters(lng);
  const listFormatters = getListFormatters(lng);
  const dateFormatters = getDateFormatters(lng);
  const relativeTimeFormatters = getRelativeTimeFormatters(lng);

  const numericFormatterMap = {
    // Example: 123.456 => '123'
    INTEGER: numericFormatters.formatInteger,
    // Example: 123.456 => '123.5'
    DECIMAL_ONE_DIGIT_MAX: numericFormatters.formatDecimalOneDigitMax,
    // Example: 123 => '123.0' or 123.456 => '123.5'
    DECIMAL_ONE_DIGIT_FIXED: numericFormatters.formatDecimalOneDigitFixed,
    // Example: 123.456 => '123.46'
    DECIMAL_TWO_DIGITS_MAX: numericFormatters.formatDecimalTwoDigitsMax,
    // Example: 1234567 => '1.2M'
    COMPACT_NUMBER: numericFormatters.formatCompact,
    // Example: 1234.56789 => '1,234.568 ms'
    MILLISECONDS: numericFormatters.formatMilliseconds,
    // Example: 0.123456789 => '12%'
    PERCENT_INTEGER: numericFormatters.formatPercentInteger,
    PERCENT: numericFormatters.formatPercentInteger, // Deprecated
    // Example: .87123456789 => '87%', 12.87123456789 => '1K%'
    PERCENT_COMPACT: numericFormatters.formatPercentCompact,
    // Example: .87123456789 => '87.1%', 12.87123456789 => '1.3K%'
    PERCENT_DECIMAL_ONE_DIGIT_MAX_COMPACT: numericFormatters.formatPercentDecimalOneDigitMaxCompact,
    // Example: 0.123456789 => '12.35%'
    PERCENT_DECIMAL_TWO_DIGITS_MAX: numericFormatters.formatPercentDecimalTwoDigitsMax,
    // Example: 150.123456789 => '15.01K%', not likely to show, but if user error results in
    // a bad percentage, we still need this formatter for fitting the value into smaller spaces
    PERCENT_DECIMAL_TWO_DIGITS_MAX_COMPACT:
      numericFormatters.formatPercentDecimalTwoDigitsMaxCompact,
    // Example: 1234.56789 => '1 kB'
    BYTES_INTEGER: byteFormatters.formatBytesInteger,
    // Example: 1234.56789 => '1.21 kB'
    BYTES_DECIMAL_TWO_DIGITS_MAX: byteFormatters.formatBytesDecimalTwoDigitsMax,
    BYTES: byteFormatters.formatBytesDecimalTwoDigitsMax, // Deprecated
    // Example: 1234.56789 => '1 kb/s'
    BITS_PER_SECOND_INTEGER: byteFormatters.formatBitsPerSecondInteger,
    // Example: 1234.56789 => '1.23 kb/s'
    BITS_PER_SECOND_DECIMAL_TWO_DIGITS_MAX: byteFormatters.formatBitsPerSecondDecimalTwoDigitsMax,
    // Example: 1234.56789 => '1 kB/s'
    BYTES_PER_SECOND_INTEGER: byteFormatters.formatBytesPerSecondInteger,
    // Example: 1234.56789 => '1.23 kB/s'
    BYTES_PER_SECOND_DECIMAL_TWO_DIGITS_MAX: byteFormatters.formatBytesPerSecondDecimalTwoDigitsMax,
  } satisfies FormatterMap<number>;

  const stringArrayFormatterMap = {
    // Example: ['foo', 'bar', 'baz'] => 'foo, bar, baz'
    SHORT_UNIT_LIST: listFormatters.formatShortUnitList,
    // Example: ['foo', 'bar', 'baz'] => 'foo bar baz'
    NARROW_UNIT_LIST: listFormatters.formatNarrowUnitList,
    // Example: ['foo', 'bar', 'baz'] => 'foo, bar, and baz'
    LONG_AND_LIST: listFormatters.formatLongAndList,
  } satisfies FormatterMap<string[]>;

  // Date examples all use
  // -- a new Date() taken at March 5, 2021 12:33:08 GMT-0500 (Eastern Standard Time)
  // -- workbenchLocale en-US
  // NOTE: When modifying this map, make a corresponding change within translation-require-valid-formatters.ts
  const dateFormatterMap = {
    // Example: '12 PM'
    HOUR_ONLY: dateFormatters.formatHourOnly,
    // Example: '5 PM' Deprecated! use HOUR_ONLY and pass a date and timezone
    HOUR_ONLY_UTC: dateFormatters.formatHourOnlyUTC,
    // Example: '5'
    DAY_ONLY: dateFormatters.formatDayOnly,
    // Example: 'Monday'
    WEEKDAY_ONLY: dateFormatters.formatWeekdayOnly,
    // Example: 'March'
    MONTH_ONLY: dateFormatters.formatMonthOnly,
    // Example: 'March 5'
    MONTH_AND_DAY: dateFormatters.formatMonthAndDay,
    // Example: '2021'
    YEAR_ONLY: dateFormatters.formatYearOnly,
    // Example: '12:33 PM'
    SHORT_TIME: dateFormatters.formatShortTime,
    // Example: '02:33' or '14:33'
    SHORT_TIME_24_HOUR: dateFormatters.formatShortTime24Hour,
    // Example: '3/5/2021'
    SHORT_DATE: dateFormatters.formatShortDate,
    // Example: '3/5/2021' <-- in some cases, is a different date than local date
    // Deprecated! use SHORT_DATE and pass a date and timezone
    SHORT_DATE_UTC: dateFormatters.formatShortDateUTC,
    // Example: 'Monday, 3/5/2021'
    SHORT_DATE_AND_WEEKDAY: dateFormatters.formatShortDateAndWeekday,
    // Example: '3/5/2021, 12:33 PM EST'
    SHORT_DATE_AND_TIME: dateFormatters.formatShortDateAndTime,
    // Example: '3/5/2021, 12:33 PM'
    SHORT_DATE_AND_TIME_NO_TIMEZONE: dateFormatters.formatShortDateAndTimeNoTimezone,
    // Example: '3/5/2021, 12:33 PM UTC' Deprecated! use SHORT_DATE_AND_TIME and pass a date and timezone
    SHORT_DATE_AND_TIME_UTC: dateFormatters.formatShortDateAndTimeUTC,
    // Example: '3/5/2021, 12:33:08 PM'
    SHORT_DATE_AND_MEDIUM_TIME: dateFormatters.formatShortDateAndMediumTime,
    // Example: '3/5/2021, 5:33:08 PM' Deprecated! use SHORT_DATE_AND_MEDIUM_TIME and pass a date and timezone
    SHORT_DATE_AND_MEDIUM_TIME_UTC: dateFormatters.formatShortDateAndMediumTimeUTC,
  } satisfies FormatterMap<DateFormatterInput>;

  // NOTE: When modifying this map, make a corresponding change within translation-require-valid-formatters.ts
  const relativeDateFormatters = {
    // Example: '1 day ago'
    RELATIVE_TIME_TO_NOW: relativeTimeFormatters.formatRelativeTime,
  } satisfies FormatterMap<Date>;

  // NOTE: When modifying this map, make a corresponding change within translation-require-valid-formatters.ts
  const durationFormatterMap = {
    // Example: {date1, date2} => '1 day'
    // where:
    // -- date1: a new Date() taken at March 5, 2021 12:33:08 GMT-0500 (Eastern Standard Time)
    // -- date2: a new Date() taken at March 6, 2021 12:33:08 GMT-0500 (Eastern Standard Time)
    DURATION: relativeTimeFormatters.formatDuration,
  } satisfies FormatterMap<DurationProps>;

  return {
    numericFormatterMap,
    stringArrayFormatterMap,
    dateFormatterMap,
    relativeDateFormatters,
    durationFormatterMap,
  };
});
